import React, { useContext, useEffect, useState } from "react";
import UploadCV from "./UploadCV";
import Swal from "sweetalert2";
import LoadingIcon from "../../images/Loading_icon.gif";
import { UrlContext } from "../../contexts/UrlContext";
import { AnimatePresence, motion } from "framer-motion";

export default function CV({ expertData, missing, setHasCV }) {
  const { API_URL } = useContext(UrlContext);
  const [showModal, setShowModal] = useState(false);
  const [updateDocument, setUpdateDocument] = useState(false);
  const [documents, setDocuments] = useState();
  const [loading, setLoading] = useState(false);
  const [loadingCV, setLoadingCV] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  useEffect(() => {
    if (showModal) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    return () => document.body.classList.remove("overflow-hidden");
  }, [showModal]);

  useEffect(() => {
    const token = localStorage.getItem("AccessToken");
    setLoading(true);
    fetch(`${API_URL}show-existing-CV`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ expertID: expertData?.id }),
    })
      .then((res) => res.json())
      .then((data) => {
        setDocuments(data?.cvList);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (documents?.length > 0) {
      setHasCV(true);
    }
  }, [documents]);

  const handleRead = async (item) => {
    const token = localStorage.getItem("AccessToken");
    setLoadingCV(item);
    fetch(`${API_URL}view-CV`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ fileName: item }),
    })
      .then((res) => res.blob())
      .then((blob) => {
        const fileURL = URL.createObjectURL(blob);
        window.open(fileURL, "pdf show", "width=800,height=600").focus();
        setLoadingCV("");
      })
      .catch((err) => {
        Swal.fire({
          icon: "error",
          title: err.message,
          showConfirmButton: false,
          timer: 1500,
        });
        setLoadingCV("");
      });
  };

  const updateCV = async (files) => {
    setUpdateDocument(false);
    setLoading(true);
    let errorFiles = [];

    for (const file of files) {
      try {
        const token = localStorage.getItem("AccessToken");

        const res = await fetch(`${API_URL}save-CV`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            expertID: expertData?.id,
            documentLinks: file.documentLinks,
          }),
        });

        const data = await res.json();

        if (data.status === "success") {
          setDocuments((prev) => [
            ...prev,
            `${expertData?.id}_${file.documentLinks[0].filename}.${file.documentLinks[0].fileFormat}`,
          ]);
        } else {
          errorFiles.push(file.documentLinks[0].filename);
        }
      } catch {
        errorFiles.push(file.documentLinks[0].filename);
      }
    }

    setLoading(false);

    if (errorFiles.length === 0) {
      Swal.fire({
        icon: "success",
        title: "All files uploaded successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
    } else {
      Swal.fire({
        icon: "error",
        title: `Failed to upload: ${errorFiles.join(", ")}`,
        showConfirmButton: false,
        timer: 1500,
      });
    }
  };

  const deleteCV = async (item) => {
    const token = localStorage.getItem("AccessToken");
    setLoadingCV(item);
    fetch(`${API_URL}delete-CV`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ fileName: item }),
    })
      .then((res) => res.json())
      .then((data) => {
        setLoadingCV("");
        if (data.status === "success") {
          setDocuments((prev) => prev.filter((cv) => cv !== item));
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: data.message,
            timer: 1500,
          });
        }
      });
  };

  const closeModal = () => {
    setShowModal(false);
    setTimeout(() => {
      setUpdateDocument(false);
    }, 300);
  };

  return (
    <>
      {/* CV Badge */}
      <div
        className={`relative cursor-pointer mt-0.5 px-1.5 py-0.3 rounded-full border ${
          missing ? "border-red-500 text-red-500" : "border-gray-300"
        } hover:bg-gray-100 transition`}
        onClick={() => setShowModal(true)}
        onMouseEnter={() => setTooltipOpen(true)}
        onMouseLeave={() => setTooltipOpen(false)}
      >
        <i className="fa-solid fa-file-lines text-base" />
        <span className="absolute -top-2 -right-2 text-[#365561] font-semibold text-xs w-4 h-4 flex items-center justify-center rounded-full">
          {documents?.length || 0}
        </span>
        {/* Tooltip (with Framer Motion) */}
        <AnimatePresence>
          {tooltipOpen && (
            <motion.div
              className="absolute -top-7 -left-4 -translate-x-1/2 bg-[#365561] text-white text-xs px-2 py-1 rounded shadow-md whitespace-nowrap z-500 pointer-events-none"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.3 }}
            >
              <span>CV & PL</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Shared Modal Container */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
          >
            <div className="relative bg-white rounded-2xl shadow-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6">
              <AnimatePresence mode="wait">
                {!updateDocument ? (
                  <motion.div
                    key="cv-list"
                    initial={{ x: 0, opacity: 1 }}
                    exit={{ x: -500, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h2 className="text-lg font-semibold mb-4">Documents</h2>
                    {loading ? (
                      <div className="flex justify-center p-4">
                        <img src={LoadingIcon} alt="Loading" width={30} />
                      </div>
                    ) : (
                      <ul className="space-y-2 max-h-64 overflow-y-auto">
                        {documents?.length > 0 ? (
                          documents.map((item) => (
                            <li
                              key={item}
                              className="flex justify-between items-center border rounded px-3 py-2 text-sm hover:bg-gray-50"
                            >
                              <span
                                className="cursor-pointer text-blue-600"
                                onClick={() => handleRead(item)}
                              >
                                {item.split("_")[1]}
                              </span>
                              {loadingCV === item ? (
                                <img
                                  src={LoadingIcon}
                                  alt="loading"
                                  width={25}
                                />
                              ) : (
                                <i
                                  className="fa-solid fa-trash text-red-500 cursor-pointer"
                                  onClick={() => deleteCV(item)}
                                />
                              )}
                            </li>
                          ))
                        ) : (
                          <li className="text-center text-gray-500 text-sm">
                            No documents yet.
                          </li>
                        )}
                      </ul>
                    )}
                    <div className="flex justify-end gap-2 mt-6">
                      <button
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-100"
                        onClick={closeModal}
                      >
                        Close
                      </button>
                      <button
                        className="px-4 py-2 bg-[#365561] text-white rounded-lg text-sm hover:bg-gray-700"
                        onClick={() => setUpdateDocument(true)}
                      >
                        Upload File
                      </button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="upload-cv"
                    initial={{ x: -500, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: -500, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <UploadCV
                      onFileSelected={updateCV}
                      setUpdateDocument={setUpdateDocument}
                      documents={documents}
                      expertData={expertData}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
